import { useRecipeGrid, fetchRecipes } from '@/components/RecipeGrid/RecipeGridProvider';
import { useEffect, memo } from 'react';
import { Card } from '@/components/Cards';
import type { PathTranslations } from '@/service/localization/getPathTranslations';
import type { ISuggestedData } from '@/components/Cards/types';
import { TrackingService } from '@/service/tracking/trackingService';

type Props = {
  filters?: string;
  recipeEnv: string;
  locale: string;
  facets?: string[];
  pathTranslations: PathTranslations;
  searchPageLabel: string;
  query?: string;
  difficultyCopy?: Record<string, string>;
  categoryName?: string;
  host: string;
  recipeIDs?: string[];
  userToken: string;
};

export const RecipeGrid = memo(
  ({
    recipeEnv,
    locale,
    filters,
    facets,
    pathTranslations,
    searchPageLabel,
    query,
    difficultyCopy,
    host,
    categoryName,
    recipeIDs,
    userToken,
  }: Props) => {
    const { state, dispatch } = useRecipeGrid();

    useEffect(() => {
      if (!state.indexName) return;

      let filterQuery = filters;

      // If no filters are applied from the refinement list, and we have recipeIDs, we use them as filters
      if ((!filters || filters.length === 0) && recipeIDs && recipeIDs.length > 0) {
        filterQuery = recipeIDs.map((id) => `id:${id}`).join(' OR ');
      }

      if (!state.loading && !state.isResetting) {
        fetchRecipes({
          filters: filterQuery,
          page: state.page,
          pageSize: 12,
          dispatch,
          indexName: state.indexName,
          facets,
          facetFilters: state.facetFilters,
          pathTranslations,
          searchPageLabel,
          query,
        });
      }
    }, [state.page, state.indexName, dispatch, state.facetFilters, state.isResetting]);

    useEffect(() => {
      dispatch({ type: 'SET_INDEX_CONFIG', indexConfig: { type: 'RECIPES', locale, recipeEnv } });
    }, [dispatch, locale, recipeEnv, state.sortMethod]);

    const trackCardClick = (recipe: ISuggestedData) => {
      const appliedFilterCategory =
        state.facetFilters?.map((item) => item[0].split(':')[0]).join(',') || 'na';
      const appliedFiltersCategory =
        state.facetFilters
          ?.flatMap((item) => item.map((appliedCategory) => appliedCategory.split(':')[1]))
          .join(',') || 'na';

      const difficulty = recipe?.information?.difficulty;
      const difficultyText = difficultyCopy && difficulty ? difficultyCopy[difficulty] : 'na';

      TrackingService.seeRecipeClick(
        categoryName || 'na',
        recipe?.category || 'na',
        recipe?.title || 'na',
        recipe?.id || 0,
        difficultyText,
        recipe?.information?.rating || 'na',
        recipe?.information?.preparationTime || recipe?.information?.totalTime || 'na',
        appliedFilterCategory,
        appliedFiltersCategory,
        state.sortMethod || 'na',
      );

      TrackingService.recipeCategoryPageConversionAfterFiltering(
        (window.dataLayer as Record<string, string>[]) || [],
        recipe?.objectID || '',
        recipe?.queryID || '',
        userToken,
      );
    };

    return (
      <>
        <div itemScope itemType="https://schema.org/ItemList" style={{ display: 'none' }}>
          {state.recipes?.map((recipe, index) => (
            <div
              key={recipe.id}
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              <span itemProp="position">{index + 1}</span>
              <span itemProp="url">{`https://${host}${recipe.link?.url}`}</span>
            </div>
          ))}
        </div>
        <div className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full gap-4 auto-rows-min min-h-[480px]">
            {state.recipes.map((recipe) => {
              return (
                <Card
                  key={recipe.id}
                  data={recipe}
                  cardType="medium"
                  link={recipe.link}
                  showInfo
                  height="480px"
                  copyDictionary={difficultyCopy}
                  trackingEvent={() => trackCardClick(recipe)}
                />
              );
            })}
          </div>
        </div>
      </>
    );
  },
);
